<template>
    <div class="dialer position--relative">
        <button-with-icon
            :icon="{
                component: () => import(
                    /* webpackChunkName: 'IconDialer' */ '@/components/bundles/layout/images/icons/IconDialer'
                ),
                ...( showKeyboard && {
                    class: 'js--active'
                })
            }"
            class="call-controlls__icon call-controlls__icon--middle"
            :action="{
                execute: true,
                function: () => ( showKeyboard = !showKeyboard )
            }"
        />
        <transition name="fade" mode="out-in">
            <div v-if="showKeyboard" class="dialer__keyboard position--absolute background--white">
                <div class="dialer__numbers color--green">{{ selectedNumbers }}</div>
                <div class="dialer__keys display--grid">
                    <base-button
                        v-for="number in numbers"
                        type="button"
                        :class="
                            `dialer__key background--grey--light
                            accessibility--button
                            dialer__key--${number.class}`
                        "
                        :hasHover="false"
                        :customClass="true"
                        v-html="number.key"
                        :key="number.key"
                        @click="dial( number.key )"
                    />
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
// packages
import { ref, watch, onUnmounted } from '@vue/composition-api';

// plugins
import { sendDTMF } from '@/plugins/jssip';

// hooks
import handlerClickOutside from '@/hooks/handler-click-outside';

export default {
    components: {
        ButtonWithIcon: () => import( /* webpackChunkName: 'ButtonWithIcon' */ '@/components/bundles/buttons/BaseButtonWithIcon' ),
        BaseButton: () => import( /* webpackChunkName: 'BaseButton' */ '@/components/bundles/buttons/BaseButton' )
    },
    setup () {
        // definição das keys do dialer.
        const numbers = [
            { key: 1, class: 'one' },
            { key: 2, class: 'two' },
            { key: 3, class: 'three' },
            { key: 4, class: 'four' },
            { key: 5, class: 'five' },
            { key: 6, class: 'six' },
            { key: 7, class: 'seven' },
            { key: 8, class: 'eight' },
            { key: 9, class: 'nine' },
            { key: '*', class: 'asterisk' },
            { key: 0, class: 'zero' },
            { key: '#', class: 'sharp' }
        ];

        // definição das vars reactivas.
        const selectedNumbers = ref( '' );
        const showKeyboard = ref( false );

        /**
         * [method]
         * Submissão da tecla clickada para o freeswitch.
         * Apresentação do número visualmente.
         *
         * @param {String} key
         * @since 1.0.0
         * @category Method
         * @autor Tiago Martinho
         *
        */
        const dial = key => {
            selectedNumbers.value += `${key}`;
            sendDTMF( key );
        };

        /**
         * [method]
         * Comportamento do dialer aquando o
         * click de determinadas teclas do teclado.
         *
         * @since 1.0.0
         * @category method
         * @autor Tiago Martinho
         *
        */
        const onKey = event => {
            const codeKeys = {
                48: 0,
                49: 1,
                50: 2,
                51: {
                    withoutShift: 3,
                    withShift: '#'
                },
                52: 4,
                53: 5,
                54: 6,
                55: 7,
                56: 8,
                57: 9,
                106: '*',
                187: {
                    withShift: '*'
                }
            };

            // se não houver combinação, não é necessário
            // continuar.
            if ( codeKeys[event.which] === undefined )
                return;

            const keyValue = codeKeys[event.which];

            // validação se é necessário validar se a tecla
            // 'shift' esta selecionada.
            const value = typeof keyValue !== 'object' ? keyValue : !event.shiftKey ? keyValue.withoutShift : keyValue.withShift;

            // Devido a tecla '187' é necessário
            // validar se foi gerado algum valor.
            if ( value === undefined )
                return;

            // submissão do tone.
            dial( value );

            // adicionado a animação na tecla
            document.activeElement.blur();
            const elementKey = document.querySelector( `.dialer__key--${numbers.find( ({ key }) => key === value ).class}` );
            elementKey.classList.add( 'js--active' );
            setTimeout( () => { elementKey.classList.remove( 'js--active' ); }, 100 );
        };

        /**
         * [listener]
         * Conforme o keyboard do dial é apresentado,
         * é adicionado um listener de eventos a DOM, de forma
         * a estar atento a pressão das teclas do teclado.
         *
         * @since 1.0.0
         * @category listener
         * @autor Tiago Martinho
         *
        */
        watch( () => showKeyboard.value,
            newShowKeyboard => {
                const type = newShowKeyboard ? 'addEventListener' : 'removeEventListener';
                document[type]( 'keydown', onKey );
            });

        // removido o evento das keydowns da DOM quando
        // o componente é destruído.
        onUnmounted( () => {
            document.removeEventListener( 'keydown', onKey );
        });

        // validação do click fora do dialer.
        handlerClickOutside({
            elementClass: 'dialer',
            condition: showKeyboard,
            toggle: () => { showKeyboard.value = false; }
        });

        return {
            numbers,
            selectedNumbers,
            showKeyboard,
            dial
        };
    }
};
</script>

<style lang="scss">
    .dialer {
        z-index: 2;
        &__keyboard {
            @include transform-position( 0, false, false, 20px );
            max-width: 265px;
            @include rounded-corner( 24px );
            padding: 27px;
            @include box-shadow( 0 9px 30px #00000012 );
            z-index: -1;
            &::before {
                @include pseudo();
                @include transform-position( -10px, false, false, -10px );
                @include dimensions( 90px, 90px );
                background: $bg-color;
                @include rounded-corner( 50% );
            }
        }
        &__numbers {
            @include dimensions( calc( 100% - 60px ), 70px );
            text-align: right;
            @include typography-specs( 26px, 34px );
            overflow: hidden;
            text-overflow: ellipsis;
            margin-left: auto;
        }
        &__keys {
            grid-template-columns: repeat( 3, 1fr );
            justify-items: center;
        }
        &__key {
            @include dimensions( 56px, 56px );
            @include typography-specs( 24px, 56px );
            @include rounded-corner( 50% );
            &.js--active,
            &:active {
                @include transform( translateY( 2px ) );
            }
        }
    }
</style>